import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Trophy, ClipboardList, Sparkles, ArrowRight, Target, Cloud, Dna, PenLine, Check, CalendarRange, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CopilotWidget from "@/components/CopilotWidget";
import AchievementsWidget from "@/components/AchievementsWidget";
import OnboardingChecklist from "@/components/OnboardingChecklist";

// Names that were assigned by the seed / provisioning flow — never surface these
// in the greeting. The user's chosen name still wins if they set it.
const RESERVED_NAMES = new Set(["platform owner", "platform", "owner", "admin", "user"]);

/** Compute a friendly greeting name or null (→ "Welcome"). */
function greetingName(user) {
  if (!user) return null;
  const preferred = (user.preferred_name || "").trim();
  if (preferred) return preferred.split(" ")[0];
  const nm = (user.name || "").trim();
  if (nm && !RESERVED_NAMES.has(nm.toLowerCase())) return nm.split(" ")[0];
  // Last resort: email prefix if it looks name-like (letters only, no dots/digits)
  const prefix = (user.email || "").split("@")[0] || "";
  if (prefix && /^[a-zA-Z]{2,20}$/.test(prefix)) {
    return prefix.charAt(0).toUpperCase() + prefix.slice(1);
  }
  return null;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dna, setDna] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [answer, setAnswer] = useState("");

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data)).finally(() => setLoading(false));
    api.get("/coach-dna").then(r => setDna(r.data)).catch(() => {});
    api.get("/reflections/current").then(r => { setReflection(r.data); setAnswer(r.data.answer || ""); }).catch(() => {});
  }, []);

  const saveReflection = async () => {
    if (!answer.trim()) return;
    try { await api.post("/reflections", { answer }); const { data: c } = await api.get("/reflections/current"); setReflection(c); } catch {}
  };

  if (loading) return <div className="text-gray-400">Loading dashboard…</div>;

  const { team, roster_size, last_game, last_practice, development_focus, recommendations } = data || {};
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = greetingName(user);

  // Onboarding is shown until team + at least one player + at least one practice exist.
  // Coach DNA remains a "nice to explore" step but doesn't block the transition.
  const hasTeam = !!team;
  const hasPlayers = (roster_size || 0) > 0;
  const hasPractice = !!last_practice;
  // Coach DNA counts as "started" when the coach has real signal (reflections
  // or analysed games) or has already opened the page at least once. The stock
  // placeholder strengths_summary from /coach-dna does NOT count.
  const dnaVisited = (() => {
    try { return localStorage.getItem("coachiq_visited_dna") === "1"; }
    catch { return false; }
  })();
  const hasDna = dnaVisited
    || (dna?.reflections_count || 0) > 0
    || (dna?.games_analyzed || 0) > 0;
  const onboardingComplete = hasTeam && hasPlayers && hasPractice;

  if (!onboardingComplete) {
    return (
      <OnboardingChecklist
        greetingName={firstName}
        hasTeam={hasTeam}
        hasPlayers={hasPlayers}
        hasPractice={hasPractice}
        hasDna={hasDna}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="animate-slide-up">
        <div className="text-sm text-gray-500">{greet},</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mt-1">
          {firstName ? `${firstName}. ` : ""}Ready for {team.name}?
        </h1>
      </div>

      <div className="grid md:grid-cols-12 gap-5 stagger">
        {/* Hero card */}
        <div className="md:col-span-8 card-soft p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gradient-to-br from-[#007AFF]/10 to-emerald-500/10 blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Current team
            </div>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">{team.name}</div>
              <div className="text-gray-500 font-medium">{team.age_group} · {team.season}</div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <Stat label="Roster" value={roster_size} sub="players" />
              <Stat label="Last match" value={last_game ? `${last_game.our_score}–${last_game.opp_score}` : "—"} sub={last_game ? `vs ${last_game.opponent}` : "no games yet"} />
              <Stat label="Formation" value={last_game?.formation || "—"} sub="most recent" />
            </div>
          </div>
        </div>

        {/* Focus card */}
        <div className="md:col-span-4 card-soft p-6 bg-gradient-to-br from-[#007AFF] to-blue-600 text-white overflow-hidden relative">
          <Target className="absolute -right-4 -bottom-4 w-32 h-32 text-white/10" />
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-100">Development focus</div>
          <div className="mt-2 font-display text-xl font-bold leading-tight">{development_focus}</div>
          <div className="mt-4 text-sm text-blue-100">Based on your recent game analysis.</div>
        </div>

        {/* Quick actions */}
        <div className="md:col-span-8 card-soft p-6">
          <div className="font-display font-bold text-lg mb-4">Quick actions</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction to="/season" icon={CalendarRange} label="Plan Season" color="from-violet-500 to-violet-600" testid="qa-season" />
            <QuickAction to="/game-day" icon={Flag} label="Game Day" color="from-rose-500 to-rose-600" testid="qa-game-day" />
            <QuickAction to="/games" icon={Trophy} label="Record game" color="from-blue-500 to-blue-600" testid="qa-record-game" />
            <QuickAction to="/practices" icon={ClipboardList} label="Build practice" color="from-emerald-500 to-emerald-600" testid="qa-build-practice" />
            <QuickAction to="/players" icon={Users} label="Players" color="from-amber-500 to-amber-600" testid="qa-players" />
            <QuickAction to="/coach-ai" icon={Sparkles} label="Coaching Partner" color="from-cyan-500 to-cyan-600" testid="qa-ask-ai" />
            <QuickAction to="/coach-dna" icon={Dna} label="Coach DNA" color="from-indigo-500 to-indigo-600" testid="qa-dna" />
            <QuickAction to="/reflect" icon={PenLine} label="Reflect" color="from-teal-500 to-teal-600" testid="qa-reflect" />
          </div>
        </div>

        {/* Recommendations */}
        <div className="md:col-span-4 card-soft p-6">
          <div className="font-display font-bold text-lg mb-3">AI recommendations</div>
          {recommendations && recommendations.length > 0 ? (
            <ul className="space-y-2.5">
              {recommendations.map((r, i) => (
                <li key={i} className="flex gap-2.5 text-sm">
                  <div className="w-5 h-5 rounded-lg bg-[#007AFF]/10 text-[#007AFF] flex items-center justify-center text-[11px] font-bold shrink-0">{i+1}</div>
                  <span className="text-gray-700 leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500">Record a game to unlock AI recommendations for your next practice.</div>
          )}
        </div>

        {/* Copilot proactive insights */}
        <div className="md:col-span-6"><CopilotWidget /></div>
        <div className="md:col-span-6"><AchievementsWidget /></div>

        {/* Weekly reflection widget */}
        <div className="md:col-span-6 card-soft p-6" data-testid="dashboard-reflection">
          <div className="flex items-center gap-2 mb-1"><PenLine className="w-4 h-4 text-[#007AFF]" /><div className="font-display font-bold text-lg">This week's reflection</div></div>
          <div className="text-sm text-gray-600 mt-1">{reflection?.question || "What is one thing you learned this week as a coach?"}</div>
          <textarea
            data-testid="dashboard-reflection-input"
            rows="2"
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            placeholder="One sentence is enough…"
            className="mt-3 w-full p-3 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none resize-none bg-white text-sm"
          />
          <div className="mt-2 flex items-center justify-between">
            <button data-testid="dashboard-reflection-save" onClick={saveReflection} disabled={!answer.trim()} className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#0056B3] disabled:opacity-40 text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors">
              <Check className="w-3.5 h-3.5" /> {reflection?.id ? "Update" : "Save"}
            </button>
            <Link to="/reflect" className="text-xs font-semibold text-[#007AFF] hover:underline">See all reflections →</Link>
          </div>
        </div>

        {/* Coach DNA snapshot */}
        <div className="md:col-span-6 card-soft p-6" data-testid="dashboard-dna">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Dna className="w-4 h-4 text-[#007AFF]" /><div className="font-display font-bold text-lg">Coach DNA</div></div>
            <Link to="/coach-dna" className="text-xs font-semibold text-[#007AFF] hover:underline">Open →</Link>
          </div>
          <div className="text-sm text-gray-600 mt-2 leading-relaxed line-clamp-3">{dna?.strengths_summary || "Your coaching identity emerges as you record games and reflect."}</div>
          {dna?.next_growth && (
            <div className="mt-3 bg-emerald-50 rounded-2xl p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Next growth</div>
              <div className="text-sm text-emerald-900 mt-1">{dna.next_growth}</div>
            </div>
          )}
        </div>

        {/* Last match card */}
        {last_game && (
          <div className="md:col-span-12 card-soft p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-lg">Last match recap</div>
              <Link to={`/games/${last_game.id}`} className="text-sm font-semibold text-[#007AFF] hover:underline" data-testid="last-game-link">Open →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Opponent</div>
                <div className="mt-1 font-display font-bold text-lg">{last_game.opponent}</div>
                <div className="text-sm text-gray-600 mt-1">Score {last_game.our_score}–{last_game.opp_score}</div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide flex items-center gap-1"><Cloud className="w-3 h-3" /> Conditions</div>
                <div className="mt-1 font-display font-bold text-lg">{last_game.weather || "—"}</div>
                <div className="text-sm text-gray-600 mt-1">Formation {last_game.formation}</div>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-4">
                <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Positives</div>
                <div className="mt-1 text-sm text-emerald-900 leading-relaxed line-clamp-4">{last_game.positives || "Add positives in game notes."}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold tracking-tight text-gray-900">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, color, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group relative bg-gray-50 hover:bg-white border border-transparent hover:border-gray-200 rounded-2xl p-4 flex flex-col gap-3 transition-colors">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-md`}>
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="text-sm font-semibold text-gray-900">{label}</div>
    </Link>
  );
}
